import React, { useState, useRef, useEffect } from 'react';

const TUTORIALS = [
  {
    id: 'upload',
    title: 'Uploading a Lab Report',
    subtitle: 'Get AI analysis in under 30 seconds',
    emoji: '⬆️',
    color: '#0284c7',
    colorLight: '#e0f2fe',
    steps: [
      {
        icon: '📋',
        title: 'Click "Upload Report"',
        body: 'From the sidebar, click "Upload Report." You can upload anytime — before or after a doctor\'s appointment.',
      },
      {
        icon: '📄',
        title: 'Choose your file',
        body: 'Select a PDF from your computer, or drag and drop it onto the upload area. PDF gives the best accuracy. Make sure the text is clear and not cut off.',
      },
      {
        icon: '⏳',
        title: 'Wait for analysis',
        body: 'HealthClear AI reads every value in your report. Most analyses finish in 15–30 seconds. You\'ll see a progress bar while it works.',
      },
      {
        icon: '✅',
        title: 'Review your results',
        body: 'Each biomarker is color-coded: green (normal), yellow (watch), red (flag). Click any value for a plain-English explanation of what it means.',
      },
      {
        icon: '💡',
        title: 'Use the AI summary',
        body: 'Scroll to the top of the analysis for a personalized summary. It highlights your most important findings and suggests questions to ask your doctor.',
      },
    ],
  },
  {
    id: 'trends',
    title: 'Tracking Trends Over Time',
    subtitle: 'See how your health changes across reports',
    emoji: '📈',
    color: '#0891b2',
    colorLight: '#e0f7fa',
    steps: [
      {
        icon: '📂',
        title: 'Upload multiple reports',
        body: 'Trends work automatically once you have two or more reports. Upload past reports — you can backdate them — to build a longer history.',
      },
      {
        icon: '📈',
        title: 'Open "My Trends"',
        body: 'Click "My Trends" in the sidebar. You\'ll see a list of all biomarkers that appear in more than one of your reports.',
      },
      {
        icon: '🔍',
        title: 'Select a biomarker',
        body: 'Click any biomarker — like Cholesterol or Glucose — to open its trend chart. The chart shows all your readings on a timeline.',
      },
      {
        icon: '📅',
        title: 'Filter by date range',
        body: 'Use the date filter to zoom into a specific period — such as the last 6 months or past year.',
      },
      {
        icon: '⚠️',
        title: 'Spot patterns',
        body: 'The chart marks your reference range as a shaded band. Values outside the band are highlighted so patterns are easy to see at a glance.',
      },
    ],
  },
  {
    id: 'appointment',
    title: 'Preparing for Appointments',
    subtitle: 'Walk in with the right questions',
    emoji: '🏥',
    color: '#0d9488',
    colorLight: '#e0fdf4',
    steps: [
      {
        icon: '📋',
        title: 'Open an analysis',
        body: 'Go to your uploaded reports and open an analysis. Scroll down past the biomarker list to find the Appointment Prep section.',
      },
      {
        icon: '🏥',
        title: 'Click Appointment Prep',
        body: 'Click the "Appointment Prep" button. HealthClear AI generates a focused summary of your most important findings to discuss.',
      },
      {
        icon: '❓',
        title: 'Review suggested questions',
        body: 'You\'ll get a tailored list of suggested questions based on your specific results — things your doctor may not mention unless you ask.',
      },
      {
        icon: '📤',
        title: 'Print or share',
        body: 'Use the print or copy button to save the prep notes. You can email them to yourself or bring them on your phone to the appointment.',
      },
      {
        icon: '✏️',
        title: 'Add your own notes',
        body: 'Click the notes field to add your own observations — symptoms, medications, or questions you\'ve thought of before your visit.',
      },
    ],
  },
  {
    id: 'medications',
    title: 'Medication Check',
    subtitle: 'Understand your meds and interactions',
    emoji: '💊',
    color: '#7c3aed',
    colorLight: '#f3e8ff',
    steps: [
      {
        icon: '💊',
        title: 'Navigate to Medication Check',
        body: 'Click "Medication Check" in the sidebar. This feature is available on the Active plan.',
      },
      {
        icon: '➕',
        title: 'Enter your medications',
        body: 'Type each medication name and click Add. Include supplements and over-the-counter drugs — interactions can involve any substance.',
      },
      {
        icon: '🔬',
        title: 'Run the analysis',
        body: 'Click "Check Medications." The AI reviews each drug for known interactions, common side effects, and plain-English explanations.',
      },
      {
        icon: '⚠️',
        title: 'Review interaction warnings',
        body: 'Potential interactions are highlighted in yellow or red. Each warning includes what the interaction is and what symptoms to watch for.',
      },
      {
        icon: '👩‍⚕️',
        title: 'Share with your provider',
        body: 'Always share findings with your doctor or pharmacist before making changes to your medications. This is educational, not medical advice.',
      },
    ],
  },
];

// Animated step player
function TutorialPlayer({ tutorial, onClose }) {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(true);

  const currentStep = tutorial.steps[step];
  const isLast = step === tutorial.steps.length - 1;

  const goTo = (next) => {
    if (animating) return;
    setAnimating(true);
    setVisible(false);
    setTimeout(() => {
      setStep(next);
      setVisible(true);
      setAnimating(false);
    }, 200);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden shadow-lg border mb-8"
      style={{ borderColor: tutorial.color + '33' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ backgroundColor: tutorial.color }}
      >
        <div>
          <span className="text-white font-bold text-base">{tutorial.emoji} {tutorial.title}</span>
        </div>
        <button
          onClick={onClose}
          className="text-white text-xl leading-none opacity-80 hover:opacity-100 transition-opacity"
          aria-label="Close tutorial"
        >
          ✕
        </button>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
        {tutorial.steps.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="transition-all duration-300 rounded-full"
            style={{
              height: 8,
              width: i === step ? 24 : 8,
              backgroundColor: i <= step ? tutorial.color : '#cbd5e1',
            }}
            aria-label={`Go to step ${i + 1}`}
          />
        ))}
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {step + 1} / {tutorial.steps.length}
        </span>
      </div>

      {/* Step content */}
      <div
        className="flex flex-col items-center text-center px-8 py-10 transition-all duration-200"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(8px)',
          minHeight: 240,
          backgroundColor: 'var(--tw-bg-opacity, white)',
        }}
      >
        <div className="text-6xl mb-5 select-none">{currentStep.icon}</div>
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-3">
          {currentStep.title}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-md">
          {currentStep.body}
        </p>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <button
          onClick={() => step > 0 ? goTo(step - 1) : onClose()}
          className="flex-1 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          {step === 0 ? 'Close' : '← Back'}
        </button>
        <button
          onClick={() => isLast ? onClose() : goTo(step + 1)}
          className="flex-1 py-2.5 rounded-xl text-white font-bold text-sm transition-opacity hover:opacity-90"
          style={{ backgroundColor: tutorial.color }}
        >
          {isLast ? 'Done ✓' : 'Next →'}
        </button>
      </div>
    </div>
  );
}

function TutorialCard({ tutorial, onPlay }) {
  return (
    <button
      onClick={onPlay}
      className="w-full text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-center gap-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 group"
      style={{ borderLeftWidth: 4, borderLeftColor: tutorial.color }}
    >
      {/* Icon */}
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl flex-shrink-0"
        style={{ backgroundColor: tutorial.colorLight }}
      >
        {tutorial.emoji}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-800 dark:text-slate-100 text-sm mb-0.5">
          {tutorial.title}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          {tutorial.subtitle}
        </p>
        <span
          className="text-xs font-semibold"
          style={{ color: tutorial.color }}
        >
          {tutorial.steps.length} steps
        </span>
      </div>

      {/* Play button */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
        style={{ backgroundColor: tutorial.color }}
      >
        <span className="text-white text-sm pl-0.5">▶</span>
      </div>
    </button>
  );
}

export default function TutorialsPage() {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const playerRef = useRef(null);

  const handlePlay = (tutorial) => {
    setActiveTutorial(tutorial);
    // Scroll to player
    setTimeout(() => playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          Tutorials
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Step-by-step interactive guides for every feature
        </p>
      </div>

      {/* Active tutorial player */}
      {activeTutorial && (
        <div ref={playerRef}>
          <TutorialPlayer
            tutorial={activeTutorial}
            onClose={() => setActiveTutorial(null)}
          />
        </div>
      )}

      {/* Tutorial list */}
      <div className="flex flex-col gap-3">
        {TUTORIALS.map((t) => (
          <TutorialCard
            key={t.id}
            tutorial={t}
            onPlay={() => handlePlay(t)}
          />
        ))}
      </div>
    </div>
  );
}
